import { DateTime } from 'luxon';
import { formatCustomDataForTimeTable } from 'ontraccr-common/lib/Tasks';
import { FormHelpers } from 'ontraccr-common';

import { getType } from '../forms/FormWorkflows/workflowHelpers';
import { uuid } from '../helpers/helpers';
import { WORK_ORDER_TYPES } from './ABWorkImport.constants';

export default {};

const loopingTableTypes = new Set(['table', 'dropdown']);

const workOrderItemNumberSort = (a, b) => parseInt(a.workOrderItemNumber, 10) - parseInt(b.workOrderItemNumber, 10);

const parseApproveRejectFile = (rows) => {
  const recordTypeMap = {};
  const recordMap = {};

  rows.forEach((row) => {
    const recordType = row.slice(0, 2);
    const rowParser = WORK_ORDER_TYPES[recordType];
    if (!rowParser) return;

    if (!recordTypeMap[recordType]) {
      recordTypeMap[recordType] = {
        rows: [],
      };
      recordTypeMap[recordType].columns = rowParser;
    }

    const parsedRow = {
      id: uuid(),
    };

    rowParser.forEach(({
      index, length, dataIndex, parser,
    }) => {
      const value = row.slice(index, length ? index + length : undefined);
      parsedRow[dataIndex] = parser ? parser(value) : value;
    });

    recordTypeMap[recordType].rows.push(parsedRow);
    recordMap[parsedRow.id] = parsedRow;
  });

  const baseReturn = { recordTypeMap, recordMap, isAcceptRejectFile: true };

  let error = '';

  if (recordTypeMap[97]?.rows?.length !== 1) {
    error = 'There should be exactly one Record Type 97 record in the file.';
    return {
      ...baseReturn,
      error,
    };
  }

  if (recordTypeMap[62]?.rows?.length !== recordTypeMap[97]?.rows[0]?.numberOfRecords) {
    error = 'The number of Record Type 62 records does not match the number of records in Record Type 97.';
    return {
      ...baseReturn,
      error,
    };
  }


  let batchNumber;
  recordTypeMap[62]?.rows?.forEach((row) => {
    if (!batchNumber) {
      batchNumber = row.batchTransferSequence;
    }

    if (batchNumber !== row.batchTransferSequence) {
      error = 'The batch transfer sequence is not consistent.';
      return {
        ...baseReturn,
        error,
      };
    }
  });
  return baseReturn;
};

export const parseABWorkFile = (rows) => {
  const recordTypeMap = {};
  const parentMap = {};
  const childMap = {};
  const recordMap = {};

  if (!rows) return { recordTypeMap, parentMap, recordMap, childMap };

  if (rows[0]?.startsWith('62')) {
    return parseApproveRejectFile(rows);
  }

  // Record 20 is the start of a new work order activity
  // Record 22 is the start of a new work order segment
  // Hierarchy: Record 20 -> Record 22 -> Record 25
  let lastRecord20Row;
  let lastRecord22Row;

  rows.forEach((row) => {
    const recordType = row.slice(0, 2);
    const rowParser = WORK_ORDER_TYPES[recordType];
    if (!rowParser) return;

    if (!recordTypeMap[recordType]) {
      recordTypeMap[recordType] = {
        rows: [],
      };
      recordTypeMap[recordType].columns = rowParser;
    }

    const rowId = uuid();

    const parsedRow = {
      id: rowId,
      originalId: rowId,
    };

    rowParser.forEach(({
      index, length, dataIndex, parser,
    }) => {
      const value = row.slice(index, length ? index + length : undefined);
      parsedRow[dataIndex] = parser ? parser(value) : value;
    });

    if (recordType === '20') {
      lastRecord20Row = parsedRow;
    }

    if (recordType === '22') {
      lastRecord22Row = parsedRow;
    }

    if (lastRecord20Row && recordType === '22') {
      if (!parentMap[lastRecord20Row.id]) {
        parentMap[lastRecord20Row.id] = [];
      }
      childMap[lastRecord22Row.id] = lastRecord20Row.workOrderItemNumber;
      parentMap[lastRecord20Row.id].push(lastRecord22Row);
    }

    let didAdd = false;

    if (recordType === '25' && parentMap[lastRecord20Row.id]?.length) {
      parentMap[lastRecord20Row.id].forEach((record22Row) => {
        if (!parentMap[record22Row.id]) {
          parentMap[record22Row.id] = [];
        }
        didAdd = true;
        const duplicatedRow = { ...parsedRow, id: uuid() };
        parentMap[record22Row.id].push(duplicatedRow);
        recordTypeMap[recordType].rows.push(duplicatedRow);
        recordMap[duplicatedRow.id] = duplicatedRow;

        const suffix = childMap[record22Row.id] ? `-${childMap[record22Row.id]}` : '';
        childMap[duplicatedRow.id] = `${record22Row.roadSegmentIdentifier}${suffix}`;
      });
    }

    if (didAdd) return;
    recordTypeMap[recordType].rows.push(parsedRow);
    recordMap[parsedRow.id] = parsedRow;
  });

  recordTypeMap['20']?.rows?.sort(workOrderItemNumberSort);
  recordTypeMap['22']?.rows?.sort(workOrderItemNumberSort);
  recordTypeMap['25']?.rows?.sort(workOrderItemNumberSort);
  return { recordTypeMap, parentMap, recordMap, childMap };
};

export const getSourceFields = ({
  sourceSections = [],
  validFieldTypes,
  baseFields = [],
}) => {
  const fields = baseFields ?? [];
  sourceSections?.forEach((section) => {
    section.fields?.forEach((field) => {
      const fieldType = getType(field, false);
      if (!validFieldTypes.has(fieldType)) return;

      fields.push({
        id: field.id,
        name: `${section.name} - ${field.configProps?.title}`,
        type: field.selectedType,
        subDataType: field.configProps?.subDataType,
      });
    });
  });

  return fields;
};

// We store the record type in the key
// So we need to get the relevant field id from the key
const getKeyFromBucketMapping = (bucketMappings, key) => {
  const value = bucketMappings[key];
  if (value === 'jobCode') return { recordType: null, key: 'jobCode' };
  const [recordType, bucketKey] = value?.split('-') ?? [];
  return { recordType, key: bucketKey };
};

const getValuesFromRecord = ({
  recordType,
  record20Row,
  record22Rows,
  record25Rows,
  bucketInsertMap,
}) => {
  let dataSource = [];
  if (recordType === '20') {
    dataSource = [{ id: record20Row }];
  } else if (recordType === '22') {
    dataSource = record22Rows;
  } else if (recordType === '25') {
    dataSource = record25Rows;
  }

  const seenOriginalIds = new Set();

  return dataSource
    .map((row) => {
      const formattedBucket = bucketInsertMap[row?.id];
      if (!formattedBucket) return null;

      const sections = [];
      const fields = [];

      formattedBucket.customData?.forEach((section) => {
        sections.push(section);
        section.fields?.forEach((field) => {
          fields.push(field);
          formattedBucket[field.fieldId] = formatCustomDataForTimeTable({
            response: field.response,
            type: field.type,
            title: field.title,
          });
        });
      });

      const result = {
        ...formattedBucket,
        customData: {
          fields,
          sections,
        },
      };

      if (row?.originalId && row.originalId !== row.id) {
        if (seenOriginalIds.has(row.originalId)) {
          result.isHidden = true;
        }
        seenOriginalIds.add(row.originalId);
      }

      return result;
    })
    .filter(Boolean);
};

const getBucketInserts = ({
  importRows,
  bucketMappings,
  bucketTemplateMap,
  recordMap,
  parentMap,
  childMap,
  statuses,
  jobCode = '',
}) => {
  const bucketInserts = [];
  const bucketInsertMap = {};
  const checkedTemplates = new Set();
  const templateStatusMap = {};
  const selectedRowIds = new Set(Object.values(importRows).flat());

  const record15Row = recordMap[importRows['15']?.[0]];

  Object.keys(importRows).forEach((recordType) => {
    const bucketTemplateId = bucketMappings[recordType];
    const bucketTemplate = bucketTemplateMap[bucketTemplateId];
    if (!bucketTemplate) return;

    const rows = importRows[recordType];
    rows.forEach((row) => {
      const relevantRecord = recordMap[row];
      if (!relevantRecord) return;

      const { key: nameKey, recordType: nameRecordType } = getKeyFromBucketMapping(bucketMappings, `${bucketTemplateId}-name`);
      const { key: descriptionKey, recordType: descriptionRecordType } = getKeyFromBucketMapping(bucketMappings, `${bucketTemplateId}-description`);

      const name = nameRecordType === '15' ? record15Row?.[nameKey] : relevantRecord[nameKey];
      const description = descriptionRecordType === '15' ? record15Row?.[descriptionKey] : relevantRecord[descriptionKey];

      const payload = {
        id: row,
        name,
        description,
        bucketTemplateId,
      };

      if (childMap[row]) {
        payload.nameKey = `${payload.name}-${childMap[row]}`;
      }

      if (!checkedTemplates.has(bucketTemplateId)) {
        checkedTemplates.add(bucketTemplateId);
        Object.values(statuses[bucketTemplateId] || {}).forEach(({ id, status }) => {
          if (status.toLowerCase() === 'open') {
            templateStatusMap[bucketTemplateId] = id;
          }
        });
      }

      payload.statusId = templateStatusMap[bucketTemplateId] || null;
      payload.customData = bucketTemplate.customFields?.sections?.map((section) => ({
        name: section.name,
        duplicatedParentId: null,
        fields: section.fields?.map((field) => {
          const {
            key: bucketMappingKey,
            recordType: bucketRecordType,
          } = getKeyFromBucketMapping(bucketMappings, field.id);
          let bucketMappingValue;

          if (bucketMappingKey === 'jobCode') {
            bucketMappingValue = jobCode;
          } else if (bucketRecordType === '15') {
            bucketMappingValue = record15Row?.[bucketMappingKey];
          } else {
            bucketMappingValue = relevantRecord[bucketMappingKey];
          }
          let response = { value: '' };

          if (bucketMappingValue) {
            switch (field.type) {
              case 'yes-no':
                response = { value: bucketMappingValue.toLowerCase() !== 'n' };
                break;
              case 'dateTime':
                response = { date: bucketMappingValue, timezone: DateTime.local().zoneName };
                break;
              default:
                response = { value: bucketMappingValue };
                break;
            }
          }

          return {
            duplicatedParentId: null,
            fieldId: field.id,
            sectionId: field.sectionId,
            title: field.title,
            type: field.type,
            response,
          };
        }),
      }));

      if (bucketTemplate.downstreamTypes && parentMap[row]) {
        const downstreamMap = bucketTemplate.downstreamTypes.reduce((acc, downstreamType) => {
          const parsedDownstreamType = downstreamType.split('-')[0];
          acc[parsedDownstreamType] = downstreamType;
          return acc;
        }, {});

        parentMap[row].forEach((childRow) => {
          if (!selectedRowIds.has(childRow.id)) return;

          const relevantBucketTemplate = bucketMappings[childRow.recordType];
          if (!relevantBucketTemplate) return;

          if (downstreamMap[relevantBucketTemplate]) {
            if (!payload[downstreamMap[relevantBucketTemplate]]) {
              payload[downstreamMap[relevantBucketTemplate]] = [];
            }
            payload[downstreamMap[relevantBucketTemplate]].push(childRow.id);
          }
        });
      }

      bucketInserts.push(payload);
      bucketInsertMap[row] = payload;
    });
  });

  return { bucketInserts, bucketInsertMap };
};

const sectionIdGenerator = () => uuid().replaceAll(/-/g, '');

const getFieldMappingValue = ({
  id,
  fieldMappings,
  recordMap,
  importRows,
  index,
  defaultResponse,
  jobCode = '',
}) => {
  const [
    recordType,
    key,
  ] = fieldMappings[id]?.split('-') ?? [];

  if (recordType === 'jobCode') return { value: jobCode, key: 'jobCode' };

  if (!recordType) return { value: defaultResponse };

  const relevantIndex = recordType === '20' ? index : 0;
  const firstRecordOfRecordType = recordMap[importRows[recordType]?.[relevantIndex]];
  return { value: firstRecordOfRecordType?.[key], recordType, key };
};

const constructCardPayload = ({
  fieldMappings,
  selectedBoard,
  selectedStatus,
  cardTemplates,
  importRows,
  parentMap,
  recordMap,
  bucketInsertMap,
  jobCode = '',
}) => {
  const cardPayload = {
    boardId: selectedBoard.id,
    cardTypeId: selectedBoard.cardTypeId,
    statusId: selectedStatus,
    color: '#FFFFFFFF',
    data: [],
  };

  const {
    value: cardTitle,
    recordType: cardTitleRecordType,
    key: cardTitleKey,
  } = getFieldMappingValue({
    id: 'cardTitle',
    fieldMappings,
    recordMap,
    importRows,
    index: 0,
    defaultResponse: '',
    jobCode,
  });

  cardPayload.title = cardTitle;
  if (cardTitleKey === 'workOrderNumber' && cardTitleRecordType === '15') {
    cardPayload.workOrderTitle = cardTitle;
  }

  const relevantCardTemplate = cardTemplates[selectedBoard.cardTypeId];
  if (!relevantCardTemplate) return cardPayload;

  relevantCardTemplate.fields?.forEach((section) => {
    let shouldLoop = false;

    if (section.settings?.isDuplicationEnabled) {
      shouldLoop = section.fields?.some((field) => fieldMappings[field.id] === '20') && importRows['20'];
    }

    // Amount of times to loop
    const loopTimes = shouldLoop ? Math.max(importRows[20].length, 1) : 1;

    // eslint-disable-next-line no-plusplus
    for (let i = 0; i < loopTimes; i++) {
      let templateSection = section;

      if (i > 0) {
        templateSection = FormHelpers.duplicateSection(section, sectionIdGenerator);
      }

      // Grab nth parent row as this is the looping variable;
      const record20Row = importRows['20']?.[i];

      if (!record20Row) continue;

      // Grab the related children to the parent row
      const record20Details = recordMap[record20Row];
      const record22Rows = parentMap[record20Row];
      const record25Rows = record22Rows?.flatMap((record22Row) => parentMap[record22Row?.id]);
      let sectionName = templateSection.name;

      if (shouldLoop && record20Details?.workOrderItemNumber) {
        sectionName = record20Details?.workOrderItemNumber;
      }

      cardPayload.data.push({
        id: templateSection.id,
        name: sectionName,
        duplicatedParentId: templateSection.duplicatedParentId,
        fields: templateSection.fields?.map((field) => {
          const formattedResponse = {
            fieldId: field.id,
            sectionId: templateSection.id,
            title: field.configProps?.title,
            type: field.selectedType,
            response: { value: '' },
            duplicatedParentId: field.duplicatedParentId,
          };

          const { value: fieldMappingValue, recordType, key } = getFieldMappingValue({
            id: field.duplicatedParentId ?? field.id,
            fieldMappings,
            recordMap,
            importRows,
            index: i,
            defaultResponse: { value: '' },
            jobCode,
          });

          if (key === 'workOrderNumber' && recordType === '15') {
            cardPayload.workOrderTitle = fieldMappingValue;
          }

          if (!recordType && key !== 'jobCode') return formattedResponse;

          if (fieldMappingValue || loopingTableTypes.has(field.selectedType)) {
            switch (field.selectedType) {
              case 'yes-no':
                formattedResponse.response = { value: fieldMappingValue.toLowerCase() !== 'n' };
                break;
              case 'dateTime':
                formattedResponse.response = {
                  date: fieldMappingValue,
                  timezone: DateTime.local().zoneName,
                };
                break;
              case 'table': {
                const values = getValuesFromRecord({
                  recordType,
                  record20Row,
                  record22Rows,
                  record25Rows,
                  bucketInsertMap,
                });
                formattedResponse.response = {
                  values,
                  dataType: field.configProps?.dataType,
                  columns: field.configProps?.columns,
                };
                break;
              }
              case 'dropdown': {
                const values = getValuesFromRecord({
                  recordType,
                  record20Row,
                  record22Rows,
                  record25Rows,
                  bucketInsertMap,
                });
                formattedResponse.response = { values };
                break;
              }
              default:
                formattedResponse.response = { value: fieldMappingValue };
                break;
            }
          }

          return formattedResponse;
        }),
      });
    }
  });

  return cardPayload;
};

export const prepareABWorkImportPayloads = ({
  importRows,
  parentMap,
  bucketMappings,
  fieldMappings,
  selectedBoard,
  selectedStatus,
  bucketTemplateMap,
  recordMap,
  cardTemplates,
  statuses,
  childMap,
  jobCode = '',
}) => {
  if (!fieldMappings || !selectedBoard || !selectedStatus) {
    return { bucketInserts: [], cardPayload: null };
  }

  // Extract the buckets to be created from the parameters given
  const { bucketInserts, bucketInsertMap } = getBucketInserts({
    importRows,
    bucketMappings,
    bucketTemplateMap,
    recordMap,
    parentMap,
    childMap,
    statuses,
    jobCode,
  });

  const cardPayload = constructCardPayload({
    fieldMappings,
    selectedBoard,
    selectedStatus,
    cardTemplates,
    importRows,
    parentMap,
    recordMap,
    bucketInsertMap,
    jobCode,
  });

  return { bucketInserts, cardPayload };
};

export const getJobCodeOptions = (jobCodes = []) => (
  jobCodes.reduce((acc, jobCode) => {
    if (jobCode.isRoutineWork) {
      acc.routine.push({ label: jobCode.name, value: jobCode.name });
    } else {
      acc.provisional.push({ label: jobCode.name, value: jobCode.name });
    }
    return acc;
  }, {
    routine: [],
    provisional: [],
  })
);
